import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth.tsx';
import { ToastProvider } from './components/Toast.tsx';
import { SessionProvider } from './hooks/useSession.tsx';
import { Shell } from './layout/Shell.tsx';
import { Activate } from './pages/Activate.tsx';
import { AdminApi } from './pages/AdminApi.tsx';
import { Billing } from './pages/Billing.tsx';
import { BillingTopupPage } from './pages/BillingTopupPage.tsx';
import { BillingTopupReturnPage } from './pages/BillingTopupReturnPage.tsx';
import { Dashboard } from './pages/Dashboard.tsx';
import { Docs } from './pages/Docs.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { Invite } from './pages/Invite.tsx';
import { Keys } from './pages/Keys.tsx';
import { Login } from './pages/Login.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { Notifications } from './pages/Notifications.tsx';
import { Optimization } from './pages/Optimization.tsx';
import { Organization } from './pages/Organization.tsx';
import { Pricing } from './pages/Pricing.tsx';
import { Register } from './pages/Register.tsx';
import { Requests } from './pages/Requests.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { RootRedirect } from './pages/RootRedirect.tsx';
import { Settings } from './pages/Settings.tsx';
import { Status } from './pages/Status.tsx';
import { Tickets } from './pages/Tickets.tsx';
import { Usage } from './pages/Usage.tsx';
import { VerifyEmail } from './pages/VerifyEmail.tsx';

/**
 * Top-level tenant-console route table.
 *
 * Unauthenticated routes render bare cards on a solid backdrop
 * (`Login`, `Register`, `VerifyEmail`, `ForgotPassword`,
 * `ResetPassword`). Authenticated routes live inside a nested
 * `<RequireAuth><Shell>` tree.
 *
 * The QueryClientProvider lives in `main.tsx` so tests can mount a
 * fresh client per render without re-creating the whole tree.
 */
export function App(): JSX.Element {
  return (
    <SessionProvider>
      <ToastProvider>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Invite-code activation (epic #1475) — public, and public on
              purpose: it is the entry point a salesperson hands out. */}
          <Route path="/activate" element={<Activate />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/password-reset" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Shell>
                  <Routes>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/keys" element={<Keys />} />
                    <Route path="/usage" element={<Usage />} />
                    <Route path="/organization" element={<Organization />} />
                    <Route path="/requests" element={<Requests />} />
                    <Route path="/optimization" element={<Optimization />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/billing" element={<Billing />} />
                    <Route path="/billing/topup" element={<BillingTopupPage />} />
                    <Route path="/billing/topup/return" element={<BillingTopupReturnPage />} />
                    {/*
                      This console keeps pricing at the top level, but
                      `console-web` nests it under `/billing`. Redirect
                      rather than mount a second copy: the deep link
                      resolves instead of 404ing, and `/pricing` stays
                      the single canonical URL. `replace` keeps the
                      alias out of history so Back doesn't bounce.
                    */}
                    <Route path="/billing/pricing" element={<Navigate to="/pricing" replace />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route
                      path="/settings/account"
                      element={<Settings initialSection="account" />}
                    />
                    <Route
                      path="/settings/security"
                      element={<Settings initialSection="security" />}
                    />
                    <Route
                      path="/settings/technical"
                      element={<Settings initialSection="technical" />}
                    />
                    <Route path="/docs" element={<Docs />} />
                    <Route path="/admin-api" element={<AdminApi />} />
                    <Route path="/status" element={<Status />} />
                    <Route path="/tickets" element={<Tickets />} />
                    {/*
                      No sidebar row on purpose (2026-08-21 定稿): the
                      header bell is the notification page's entrance —
                      dot when unread, click to come here.
                    */}
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Shell>
              </RequireAuth>
            }
          />
        </Routes>
      </ToastProvider>
    </SessionProvider>
  );
}
